<template>
  <div class="invitePage" id="invitePage" ref="invitePage">
    <div class="content-stream">

      <!-- 邀请主卡 -->
      <section class="card hero-card" data-purpose="referral-hero-card">
        <div class="hero-tag-wrap">
          <span class="hero-tag">{{ $t('lang.invite.referralProgram') }}</span>
        </div>
        <h2 class="hero-title">{{ $t('lang.invite.inviteFriendsEarn') }}</h2>
        <div class="hero-amount">{{ $t('lang.invite.earnBc', {amount: separateThousandInteger(hero.earn)}) }}</div>
        <p class="hero-desc">{{ $t('lang.invite.whenFriendsAddFunds') }}</p>

        <button class="btn-share" type="button" @click="onShare">
          <svg viewBox="0 0 24 24" class="ic-share" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
            <line x1="8.59" x2="15.42" y1="13.51" y2="17.49"/><line x1="15.41" x2="8.59" y1="6.51" y2="10.49"/>
          </svg>
          <span>{{ $t('lang.invite.shareInviteLink') }}</span>
        </button>

        <button class="btn-copy" type="button" @click="onCopyLink">
          <svg viewBox="0 0 24 24" class="ic-copy" fill="none" stroke="currentColor" stroke-width="2">
            <rect height="13" rx="2" ry="2" width="13" x="9" y="9"/>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
          </svg>
          <span>{{ $t('lang.invite.copyLink') }}</span>
        </button>
      </section>

      <!-- 邀请奖励 -->
      <section class="card rewards-card" data-purpose="invite-rewards-card">
        <div class="sec-head">
          <h2 class="sec-title">{{ $t('lang.invite.inviteRewards') }}</h2>
          <p class="sec-sub">{{ $t('lang.invite.unlockBonuses') }}</p>
        </div>

        <div class="tier-list">
          <div class="tier" :class="'tier--' + t.state" v-for="t in tiers" :key="t.n">
            <div class="tier-left">
              <div class="tier-icon">
                <svg v-if="t.state === 'claimable'" viewBox="0 0 24 24" class="ic-check" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span v-else-if="t.state === 'next'" class="tier-num">{{ t.n }}</span>
                <svg v-else viewBox="0 0 24 24" class="ic-lock" fill="none" stroke="currentColor" stroke-width="2">
                  <rect height="11" rx="2" ry="2" width="18" x="3" y="11"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <div class="tier-text">
                <div class="tier-name-row">
                  <span class="tier-name">{{ $t('lang.invite.friendsTier', {n: t.n}) }}</span>
                  <span class="tier-next-chip" v-if="t.state === 'next'">{{ $t('lang.invite.next') }}</span>
                </div>
                <span class="tier-bonus">{{ $t('lang.invite.bonusBc', {amount: separateThousandInteger(t.bonus)}) }}</span>
              </div>
            </div>
            <button v-if="t.state === 'claimable'" class="tier-claim" type="button" @click="onClaim(t)">
              {{ $t('lang.invite.claim') }}
            </button>
            <span v-else-if="t.state === 'next'" class="tier-needed">{{ $t('lang.invite.moreNeeded', {n: t.remaining}) }}</span>
            <span v-else class="tier-locked-label">{{ $t('lang.invite.locked') }}</span>
          </div>
        </div>

        <div class="tbc">{{ $t('lang.invite.toBeContinued') }}</div>

        <div class="recur">
          <div class="recur-left">
            <div class="recur-icon">
              <svg viewBox="0 0 24 24" class="ic-gift" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 12 20 22 4 22 4 12"/><rect height="5" width="20" x="2" y="7"/>
                <line x1="12" x2="12" y1="22" y2="7"/>
                <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
                <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
              </svg>
            </div>
            <div class="recur-text">
              <span class="recur-name">{{ $t('lang.invite.everyMoreFriends', {n: recurring.every}) }}</span>
              <span class="recur-bonus">{{ $t('lang.invite.bonusBc', {amount: separateThousandInteger(recurring.bonus)}) }}</span>
            </div>
          </div>
          <span class="recur-progress">{{ $t('lang.invite.tierProgress', {cur: recurring.cur, total: recurring.total}) }}</span>
        </div>
      </section>

      <!-- 推荐概览 -->
      <section class="card overview-card" data-purpose="referral-overview-card">
        <div class="ov-head">
          <h2 class="ov-title">{{ $t('lang.invite.referralOverview') }}</h2>
          <a class="ov-link" href="javascript:;" @click="onViewInvitees">
            {{ $t('lang.invite.viewInvitees') }}
            <svg viewBox="0 0 24 24" class="ic-chev" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </a>
        </div>

        <div class="ov-grid">
          <div class="ov-cell">
            <span class="ov-num">{{ separateThousandInteger(overview.invited) }}</span>
            <span class="ov-cap">{{ $t('lang.invite.statInvited') }}</span>
          </div>
          <div class="ov-cell">
            <span class="ov-num">{{ separateThousandInteger(overview.active) }}</span>
            <span class="ov-cap">{{ $t('lang.invite.statActive') }}</span>
          </div>
          <div class="ov-cell">
            <span class="ov-num brand">{{ separateThousandInteger(overview.bcEarned) }}</span>
            <span class="ov-cap">{{ $t('lang.invite.statBcEarned') }}</span>
          </div>
        </div>

        <div class="ov-note">
          <svg viewBox="0 0 24 24" class="ic-info" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="16" y2="12"/><line x1="12" x2="12.01" y1="8" y2="8"/>
          </svg>
          <span>{{ $t('lang.invite.activeDefinition') }}</span>
        </div>
      </section>

      <!-- 玩法说明 -->
      <section class="card hiw-card" data-purpose="how-it-works-card">
        <h2 class="hiw-title">{{ $t('lang.invite.howItWorks') }}</h2>
        <div class="hiw-list">
          <div class="hiw-step">
            <h3 class="hiw-step-title">{{ $t('lang.invite.step1Title') }}</h3>
            <p class="hiw-step-desc">{{ $t('lang.invite.step1Desc') }}</p>
          </div>
          <div class="hiw-step">
            <h3 class="hiw-step-title">{{ $t('lang.invite.step2Title') }}</h3>
            <p class="hiw-step-desc">{{ $t('lang.invite.step2Desc') }}</p>
          </div>
          <div class="hiw-step">
            <h3 class="hiw-step-title">{{ $t('lang.invite.step3Title') }}</h3>
            <p class="hiw-step-desc">{{ $t('lang.invite.step3Desc') }}</p>
          </div>
        </div>
      </section>

    </div>
  </div>
</template>

<script>
import {mapMutations} from 'vuex'
import {separateThousandInteger} from '@/utils'

export default {
  name: 'Invite',
  components: {},
  data () {
    return {
      userInfo: JSON.parse(localStorage.getItem('userInfo')),
      // 静态占位数据：文案与数值取自 Stitch screen e966e1ad (Invite, Strict 360px Root)。
      // 后端邀请接口尚未提供，service/service2 不在本次改动范围；接口就绪后改为 mounted 请求并填充同名结构。
      hero: {earn: 1800},
      tiers: [
        {n: 3, bonus: 1800, state: 'claimable'},
        {n: 6, bonus: 3600, state: 'next', remaining: 3},
        {n: 10, bonus: 6000, state: 'locked'},
        {n: 20, bonus: 10000, state: 'locked'}
      ],
      recurring: {every: 5, bonus: 2000, cur: 1, total: 5},
      overview: {invited: 38, active: 30, bcEarned: 21400}
    }
  },
  watch: {},
  async mounted () {
    // TODO: 接入后端 Invite 数据（可邀请奖励 / tier 进度 / 概览统计）
  },
  beforeDestroy () {
  },
  methods: {
    separateThousandInteger,
    ...mapMutations({
      setCurrentRoute: 'setCurrentRoute',
      setInternationName: 'setInternationName',
      setInternation: 'setInternation'
    }),
    onShare () {
      // TODO: 调用原生/App 分享邀请链接
    },
    onCopyLink () {
      // TODO: 复制邀请链接到剪贴板
    },
    onClaim (tier) {
      // TODO: 领取该 tier 的 BC 奖励
    },
    onViewInvitees () {
      // TODO: 进入被邀请人明细列表（Stitch screen 930fe6b3）
    }
  }
}
</script>

<style lang='less' scoped>
.invitePage {
  height: 100dvh;
  overflow: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  position: relative;
  background: var(--stitch-page-bg);
  padding-top: calc(60vw / 3.6); /* App 壳层 TopBar 占位 */
}

.content-stream {
  display: flex;
  flex-direction: column;
  gap: calc(14vw / 3.6);
  padding: calc(4vw / 3.6) calc(16vw / 3.6) calc(88vw / 3.6); /* 底部让开 FooterGuide */
}

/* ---- 通用卡片 ---- */
.card {
  background: var(--stitch-surface);
  border: calc(1vw / 3.6) solid var(--stitch-card-border);
  border-radius: var(--stitch-radius-xl);
  box-shadow: 0 calc(1vw / 3.6) calc(2vw / 3.6) rgba(0, 0, 0, .05);
  padding: calc(16vw / 3.6);
}

/* ---- 邀请主卡 ---- */
.hero-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.hero-tag-wrap { align-self: flex-start; margin-bottom: calc(12vw / 3.6); }
.hero-tag {
  display: inline-block;
  background: var(--stitch-brand-soft);
  color: var(--stitch-brand);
  font-size: calc(11vw / 3.6);
  font-weight: 700;
  padding: calc(4vw / 3.6) calc(10vw / 3.6);
  border-radius: var(--stitch-radius-full);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.hero-title { align-self: flex-start; font-size: calc(15vw / 3.6); font-weight: 700; color: var(--stitch-text-primary); line-height: 1.375; }
.hero-amount { align-self: flex-start; font-size: calc(26vw / 3.6); font-weight: 700; color: var(--stitch-text-primary); line-height: 1.25; margin-top: calc(2vw / 3.6); }
.hero-desc { align-self: flex-start; font-size: calc(12vw / 3.6); color: var(--stitch-text-secondary); margin-top: calc(4vw / 3.6); text-align: left; }

.btn-share {
  width: 100%;
  margin-top: calc(16vw / 3.6);
  background: var(--stitch-brand);
  color: var(--stitch-surface);
  font-weight: 500;
  font-size: calc(14vw / 3.6);
  padding: calc(12vw / 3.6) calc(16vw / 3.6);
  border: none;
  border-radius: var(--stitch-radius-xl);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: calc(8vw / 3.6);
  box-shadow: 0 calc(1vw / 3.6) calc(2vw / 3.6) rgba(0, 81, 223, .18);
  cursor: pointer;
  transition: background-color .12s ease;
  -webkit-tap-highlight-color: transparent;
}
.btn-share:active { background: var(--stitch-brand-active); }
.ic-share { width: calc(16vw / 3.6); height: calc(16vw / 3.6); }

.btn-copy {
  margin-top: calc(14vw / 3.6);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: calc(6vw / 3.6);
  background: none;
  border: none;
  font-size: calc(12vw / 3.6);
  font-weight: 500;
  color: var(--stitch-text-primary);
  padding: 0 0 calc(4vw / 3.6);
  cursor: pointer;
  transition: color .12s ease;
  -webkit-tap-highlight-color: transparent;
}
.btn-copy:active { color: var(--stitch-brand); }
.ic-copy { width: calc(14vw / 3.6); height: calc(14vw / 3.6); }

/* ---- 邀请奖励 ---- */
.rewards-card { display: flex; flex-direction: column; }
.sec-head { margin-bottom: calc(14vw / 3.6); }
.sec-title { font-size: calc(16vw / 3.6); font-weight: 700; color: var(--stitch-text-primary); }
.sec-sub { font-size: calc(12vw / 3.6); color: var(--stitch-text-secondary); margin-top: calc(2vw / 3.6); }

.tier-list { display: flex; flex-direction: column; gap: calc(10vw / 3.6); }
.tier {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: calc(10vw / 3.6);
  border-radius: var(--stitch-radius-xl);
  border: calc(1vw / 3.6) solid var(--stitch-card-border);
  background: var(--stitch-surface);
}
.tier--claimable { border-color: var(--stitch-brand-lighter); background: var(--stitch-tier-active-bg); }
.tier--locked { opacity: .85; }

.tier-left { display: flex; align-items: center; gap: calc(12vw / 3.6); min-width: 0; }
.tier-icon { width: calc(32vw / 3.6); height: calc(32vw / 3.6); border-radius: var(--stitch-radius-full); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.tier--claimable .tier-icon { background: var(--stitch-brand); color: var(--stitch-surface); }
.tier--next .tier-icon { background: var(--stitch-brand-lighter); color: var(--stitch-brand); }
.tier--locked .tier-icon { background: var(--stitch-chip-bg); color: var(--stitch-text-weak); }
.ic-check { width: calc(16vw / 3.6); height: calc(16vw / 3.6); }
.ic-lock { width: calc(16vw / 3.6); height: calc(16vw / 3.6); }
.tier-num { font-size: calc(12vw / 3.6); font-weight: 700; }

.tier-text { display: flex; flex-direction: column; text-align: left; min-width: 0; }
.tier-name-row { display: flex; align-items: center; gap: calc(6vw / 3.6); }
.tier-name { font-size: calc(12vw / 3.6); font-weight: 700; color: var(--stitch-text-primary); }
.tier--locked .tier-name { font-weight: 500; color: var(--stitch-text-secondary); }
.tier-next-chip { background: var(--stitch-brand-soft); color: var(--stitch-brand); font-size: calc(10vw / 3.6); font-weight: 600; padding: calc(1vw / 3.6) calc(6vw / 3.6); border-radius: calc(4vw / 3.6); }
.tier-bonus { font-size: calc(12vw / 3.6); font-weight: 600; color: var(--stitch-brand); }
.tier--locked .tier-bonus { font-weight: 400; color: var(--stitch-text-weak); }

.tier-claim {
  background: var(--stitch-brand);
  color: var(--stitch-surface);
  font-size: calc(12vw / 3.6);
  font-weight: 600;
  padding: calc(6vw / 3.6) calc(16vw / 3.6);
  border: none;
  border-radius: var(--stitch-radius-lg);
  box-shadow: 0 calc(1vw / 3.6) calc(2vw / 3.6) rgba(0, 81, 223, .18);
  cursor: pointer;
  flex-shrink: 0;
}
.tier-claim:active { background: var(--stitch-brand-active); }
.tier-needed { font-size: calc(11vw / 3.6); color: var(--stitch-text-secondary); background: var(--stitch-chip-bg); padding: calc(4vw / 3.6) calc(10vw / 3.6); border-radius: var(--stitch-radius-full); font-weight: 500; flex-shrink: 0; }
.tier-locked-label { font-size: calc(12vw / 3.6); color: var(--stitch-text-weak); flex-shrink: 0; }

.tbc { text-align: center; margin: calc(12vw / 3.6) 0; font-size: calc(11vw / 3.6); color: var(--stitch-text-weak); font-weight: 400; letter-spacing: 0.025em; }

.recur {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: calc(10vw / 3.6);
  border-radius: var(--stitch-radius-xl);
  border: calc(1vw / 3.6) solid var(--stitch-protection-border);
  background: var(--stitch-recur-bg);
}
.recur-left { display: flex; align-items: center; gap: calc(12vw / 3.6); min-width: 0; }
.recur-icon { width: calc(32vw / 3.6); height: calc(32vw / 3.6); border-radius: var(--stitch-radius-lg); background: var(--stitch-brand-lighter); color: var(--stitch-brand); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.ic-gift { width: calc(16vw / 3.6); height: calc(16vw / 3.6); }
.recur-text { display: flex; flex-direction: column; text-align: left; }
.recur-name { font-size: calc(12vw / 3.6); font-weight: 700; color: var(--stitch-text-primary); }
.recur-bonus { font-size: calc(12vw / 3.6); font-weight: 700; color: var(--stitch-brand); }
.recur-progress { font-size: calc(12vw / 3.6); font-weight: 700; color: var(--stitch-brand); background: var(--stitch-brand-lighter); padding: calc(2vw / 3.6) calc(10vw / 3.6); border-radius: var(--stitch-radius-full); flex-shrink: 0; }

/* ---- 推荐概览 ---- */
.overview-card { display: flex; flex-direction: column; }
.ov-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: calc(12vw / 3.6); }
.ov-title { font-size: calc(14vw / 3.6); font-weight: 700; color: var(--stitch-text-primary); }
.ov-link { font-size: calc(12vw / 3.6); font-weight: 500; color: var(--stitch-brand); display: flex; align-items: center; gap: calc(1vw / 3.6); text-decoration: none; }
.ic-chev { width: calc(12vw / 3.6); height: calc(12vw / 3.6); }

.ov-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: calc(8vw / 3.6); }
.ov-cell { background: var(--stitch-steps-panel); border-radius: var(--stitch-radius-xl); padding: calc(10vw / 3.6); display: flex; flex-direction: column; align-items: center; justify-content: center; }
.ov-num { font-size: calc(20vw / 3.6); font-weight: 700; color: var(--stitch-text-primary); line-height: 1.2; }
.ov-num.brand { font-size: calc(18vw / 3.6); color: var(--stitch-brand); }
.ov-cap { font-size: calc(11vw / 3.6); color: var(--stitch-text-secondary); margin-top: calc(2vw / 3.6); }

.ov-note { margin-top: calc(12vw / 3.6); display: flex; align-items: center; justify-content: center; gap: calc(4vw / 3.6); font-size: calc(11vw / 3.6); color: var(--stitch-text-secondary); }
.ic-info { width: calc(14vw / 3.6); height: calc(14vw / 3.6); flex-shrink: 0; }

/* ---- 玩法说明 ---- */
.hiw-card { margin-bottom: calc(2vw / 3.6); }
.hiw-title { font-size: calc(14vw / 3.6); font-weight: 700; color: var(--stitch-text-primary); margin-bottom: calc(12vw / 3.6); text-align: left; }
.hiw-list { display: flex; flex-direction: column; gap: calc(12vw / 3.6); text-align: left; }
.hiw-step-title { font-size: calc(12vw / 3.6); font-weight: 700; color: var(--stitch-text-primary); }
.hiw-step-desc { font-size: calc(11vw / 3.6); color: var(--stitch-text-secondary); margin-top: calc(2vw / 3.6); line-height: 1.5; }
</style>



// WEBPACK FOOTER //
// src/pages/Invite/index.vue